// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { clearCache } from '../../lib/indexedDb.js';
import { generateGnuCashXml } from '../../lib/gnucashXml.js';
import { exportTextFile } from '../../native/exportFile.js';
import { MobileTopBar } from './MobileTopBar.js';
import { ChevronRightIcon } from './icons.js';

// DECISION (Phase 3): the "More" tab is the overflow home for everything that
// doesn't earn a primary tab — search, budgets, scheduled, import, portfolio,
// settings — plus account-level actions. Simple full-width touch rows, grouped.
export function MoreScreen() {
  const { t, i18n } = useTranslation(['common', 'mobile', 'errors', 'scheduled', 'import']);
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function handleExport() {
    setBusy(true);
    try {
      const xml = generateGnuCashXml(state.events);
      await exportTextFile(
        `hushold-${new Date().toISOString().slice(0, 10)}.gnucash`,
        xml,
        'application/xml',
      );
    } catch (err) {
      alert(t('mobile:errors.exportFailed', { message: err instanceof Error ? err.message : String(err) }));
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    await clearCache();
    dispatch({ type: 'AUTH_LOGOUT' });
  }

  return (
    <>
      <MobileTopBar title={t('nav.more')} />
      <div className="flex-1 overflow-y-auto native-scroll pb-tabbar">
        <div className="px-4 py-4 flex items-center gap-3 border-b border-border">
          <div className="w-10 h-10 bg-accent-muted border border-accent text-accent flex items-center justify-center font-semibold">
            {state.auth.username?.charAt(0).toLocaleUpperCase(i18n.language) ?? '?'}
          </div>
          <div>
            <p className="text-md text-text">{state.auth.username}</p>
            <p className="text-xs text-text-muted">{t('encrypted')}</p>
          </div>
        </div>

        <Group label={t('nav.manage')}>
          <Row label={t('nav.search')} onClick={() => navigate('/search')} />
          <Row label={t('nav.budgets')} onClick={() => navigate('/budgets')} />
          <Row label={t('scheduled:title')} onClick={() => navigate('/scheduled-transactions')} />
          <Row label={t('nav.portfolio')} onClick={() => navigate('/portfolio')} />
          <Row label={t('import:page.title')} onClick={() => navigate('/import')} />
        </Group>

        <Group label={t('nav.account')}>
          <Row label={t('nav.settings')} onClick={() => navigate('/settings')} />
          <Row label={busy ? t('mobile:moreScreen.exporting') : t('mobile:moreScreen.exportGnuCash')} onClick={() => void handleExport()} />
        </Group>

        <Group label="">
          <button
            onClick={() => void handleLogout()}
            className="w-full text-left px-4 h-12 touch-row flex items-center text-negative active:bg-bg-elevated border-b border-border"
          >
            {t('actions.signOut')}
          </button>
        </Group>

        <p className="text-center text-xs text-text-subtle py-6">{t('appName')}</p>
      </div>
    </>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      {label && (
        <h2 className="px-4 pt-4 pb-1 text-xs font-medium text-text-subtle uppercase tracking-widest">
          {label}
        </h2>
      )}
      {children}
    </section>
  );
}

function Row({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between gap-3 px-4 h-12 touch-row border-b border-border text-left active:bg-bg-elevated"
    >
      <span className="text-md text-text">{label}</span>
      <ChevronRightIcon size={16} className="text-text-muted shrink-0" />
    </button>
  );
}
